import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { FiClock, FiCheck, FiTrendingUp, FiFileText } from 'react-icons/fi';
import { EmptyState } from '@/components/custom/empty-state';
import { PeriodFilter } from './PeriodFilter';
import { ExercisesMetricsChartPerformance } from '../exercises-metrics/exercises-metrics-chart-performance';
import { ExercisesMetricsChartAverageAccess } from '../exercises-metrics/exercises-metrics-chart-average-access';

const WEEK_OPTIONS = [
  { value: 0, label: 'Hoje' },
  { value: 1, label: 'Semana Atual' },
  { value: 4, label: 'Último mês' },
  { value: 12, label: '3 meses' }
];

const ICON_STYLE = {
  clock: { Icon: FiClock, bg: 'var(--color-bg-teal-soft)', color: 'var(--color-primary)' },
  check: { Icon: FiCheck, bg: 'var(--color-bg-lilac)', color: 'var(--color-secondary)' },
  arrow: { Icon: FiTrendingUp, bg: 'var(--color-bg-mission-soft)', color: 'var(--color-mission-medium)' }
};
const DEFAULT_ICON_STYLE = { Icon: FiFileText, bg: 'var(--color-bg-mission-soft)', color: 'var(--color-mission-medium)' };

const PERIOD_TEXT = {
  dia: 'dia anterior',
  semana: 'semana passada',
  mes: 'mes passado'
};

export function ExercisesMetricsPlayful() {
  const client = useClient();
  const [week, setWeek] = useState(0);
  const [metrics, setMetrics] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [averageAccess, setAverageAccess] = useState({ items: [], averageItems: [] });

  const getMetrics = async () => {
    const { data, error } = await client.fetch(`reports/question_books/metrics${week === 0 ? '' : `?week=${week}`}`);
    if (error) toast.error('Não foi possível carregar os indicadores.');
    else setMetrics(data || []);
  };

  const getPerformance = async () => {
    const { data, error } = await client.fetch(`reports/question_books/answer_performance?week=${week}`);
    if (error) toast.error('Não foi possível carregar o aproveitamento.');
    else setPerformance(data || []);
  };

  const getAverageAccess = async () => {
    const { data, error } = await client.fetch(`reports/question_books/average_answering_time?week=${week}`);
    if (error) toast.error('Não foi possível carregar o tempo médio de resposta.');
    else setAverageAccess(data || { items: [], averageItems: [] });
  };

  useEffect(() => {
    getMetrics();
    getPerformance();
    getAverageAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week]);

  const totalDuration = (averageAccess.items || []).reduce((sum, item) => sum + item[1], 0);

  return (
    <div>
      <div className="mb-6">
        <PeriodFilter
          options={WEEK_OPTIONS}
          value={week}
          onChange={setWeek}
        />
      </div>

      <div className="mb-6 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
        {metrics.map(metric => {
          const { Icon, bg, color } = ICON_STYLE[metric.icon] || DEFAULT_ICON_STYLE;
          const trendColor = metric.percent < 0 ? 'text-danger' : 'text-success';
          const periodText = PERIOD_TEXT[metric.period] || PERIOD_TEXT.mes;

          return (
            <div
              key={metric.id}
              className="flex items-center gap-4 rounded-[16px] bg-white p-4 shadow-card"
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
                style={{ background: bg }}
              >
                <Icon
                  className="h-5 w-5"
                  style={{ color }}
                />
              </span>
              <div className="min-w-0">
                <span className="block text-xs font-bold uppercase tracking-wide text-neutral-400">{metric.label}</span>
                <span
                  className="text-lg font-extrabold"
                  style={{ color: '#222731' }}
                >
                  {metric.value}
                </span>
                <span className={`block text-xs font-semibold ${trendColor}`}>
                  {metric.percent}% {metric.percent < 0 ? 'a menos' : 'a mais'} que o {periodText}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mb-6 rounded-[20px] bg-white p-5 shadow-card">
        <h3
          className="mb-4 text-base font-bold"
          style={{ color: '#222731' }}
        >
          Aproveitamento
        </h3>
        <ExercisesMetricsChartPerformance chartData={performance} />
      </div>

      <div className="mb-6 rounded-[20px] bg-white p-5 shadow-card">
        <h3
          className="mb-4 text-base font-bold"
          style={{ color: '#222731' }}
        >
          Média móvel de tempo respondendo
        </h3>
        {totalDuration > 0 ? (
          <ExercisesMetricsChartAverageAccess
            color="#442B8C"
            chartData={averageAccess}
            valueSuffix={false}
          />
        ) : (
          <EmptyState
            variant="plain"
            type="register"
            text="Nenhum acesso encontrado para esse período"
          />
        )}
      </div>
    </div>
  );
}
